import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { getShotAnchors, createShotAnchor, deleteShotAnchor, autoPlanAnchors } from "@/services/shotService";
import AutoStoryboardPreflight from "@/components/AutoStoryboardPreflight";
import AutoStoryboardProgress from "@/components/AutoStoryboardProgress";
import { redetectScenes } from "@/services/scriptService";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { IconScenes } from "@/components/Icons";
import {
  Loader2, Wand2, PanelLeftClose, PanelLeft,
  Film, Settings2, Plus, Search, Brain, ShieldCheck, Sparkles, BookOpen
} from "lucide-react";
import EmbeddedQuickDraw from "@/components/EmbeddedQuickDraw";
import AnchoredScriptView from "@/components/AnchoredScriptView";
import AnchorShotPanel from "@/components/AnchorShotPanel";
import ScriptIntelligence from "@/components/ScriptIntelligence";
import EnhancedSceneDesignTab from "@/components/EnhancedSceneDesignTab";
import ShotBlockingView from "@/components/ShotBlockingView";
import ContinuityReport from "@/components/ContinuityReport";
import VisualScreenplayView from "@/components/VisualScreenplayView";
import SceneCard from "@/components/scenes/SceneCard";
import {
  SHOT_TYPES, CAMERA_ANGLES, CAMERA_MOVEMENTS, LIGHTING_STYLES, LENS_OPTIONS
} from "@/constants/shotSettings";
import { useNotifications, NOTIFICATION_TYPES, NOTIFICATION_CATEGORIES } from "@/contexts/NotificationContext";

export default function ScenesPage({ script, characters, locations = [], onUpdate }) {
  const [selectedScene, setSelectedScene] = useState(null);
  const [detecting, setDetecting] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [scriptPanelCollapsed, setScriptPanelCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState("design");
  const [showQuickDraw, setShowQuickDraw] = useState(false);
  const [showBlocking, setShowBlocking] = useState(false);
  const [sceneSearch, setSceneSearch] = useState("");

  // Shot Anchors state
  const [anchors, setAnchors] = useState([]);
  const [selectedAnchorId, setSelectedAnchorId] = useState(null);
  const [loadingAnchors, setLoadingAnchors] = useState(false);
  const [autoPlanning, setAutoPlanning] = useState(false);

  // Auto Storyboard state
  const [showAutoGenerate, setShowAutoGenerate] = useState(false);
  const [autoGenScene, setAutoGenScene] = useState(null); // null = full movie, number = specific scene
  const [showProgress, setShowProgress] = useState(false);

  const { addNotification } = useNotifications();
  const scenes = script?.scenes || [];

  const filteredScenes = scenes.filter(s =>
    s.heading?.toLowerCase().includes(sceneSearch.toLowerCase()) ||
    s.location?.toLowerCase().includes(sceneSearch.toLowerCase()) ||
    s.content?.toLowerCase().includes(sceneSearch.toLowerCase()) ||
    String(s.number).includes(sceneSearch)
  );

  // ========== SHOT ANCHORS ==========

  useEffect(() => {
    if (selectedScene && script?.project_id && (activeTab === "shots" || activeTab === "screenplay")) {
      loadAnchors();
    }
  }, [selectedScene?.number, script?.project_id, activeTab]);

  const loadAnchors = async () => {
    if (!selectedScene || !script?.project_id) return;
    setLoadingAnchors(true);
    try {
      const data = await getShotAnchors(script.project_id, selectedScene.number);
      setAnchors(data || []);
    } catch (err) {
      console.error("Failed to load anchors:", err);
      setAnchors([]);
    } finally {
      setLoadingAnchors(false);
    }
  };

  const handleAnchorCreate = async (anchorData) => {
    if (!selectedScene || !script?.project_id) return;
    try {
      const data = await createShotAnchor({
        project_id: script.project_id,
        scene_number: selectedScene.number,
        selected_text: anchorData.selected_text,
        start_offset: anchorData.start_offset,
        end_offset: anchorData.end_offset,
      });

      setAnchors(prev => [...prev, data]);
      setSelectedAnchorId(data.id);
      toast.success("Shot anchor created! Design your shot in the panel.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create anchor");
    }
  };

  const handleAnchorDelete = async (anchorId) => {
    try {
      await deleteShotAnchor(anchorId);
      setAnchors(prev => prev.filter(a => a.id !== anchorId));
      if (selectedAnchorId === anchorId) setSelectedAnchorId(null);
      toast.success("Anchor deleted");
    } catch (err) {
      toast.error("Failed to delete anchor");
    }
  };

  const handleAutoShotPlan = async () => {
    if (!selectedScene || !script?.project_id || !selectedScene.content) {
      toast.error("Select a scene with script content first");
      return;
    }
    setAutoPlanning(true);
    try {
      const data = await autoPlanAnchors({
        project_id: script.project_id,
        scene_number: selectedScene.number,
        script_content: selectedScene.content,
      });

      setAnchors(data.anchors || []);
      toast.success(`AI created ${data.count} shot anchors! Review and generate each.`);
      addNotification({
        type: NOTIFICATION_TYPES.SUCCESS,
        category: NOTIFICATION_CATEGORIES.SCENES,
        title: 'Auto Shot Plan Complete',
        message: `Created ${data.count} shot anchors for Scene ${selectedScene.number}`
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Auto plan failed");
    } finally {
      setAutoPlanning(false);
    }
  };

  const handleAutoGenerateScene = (sceneNum) => {
    setAutoGenScene(sceneNum);
    setShowAutoGenerate(true);
  };

  const handleAutoGenerateFull = () => {
    setAutoGenScene(null);
    setShowAutoGenerate(true);
  };

  const handleGenerationStarted = () => {
    setShowProgress(true);
  };

  const handleGenerationComplete = () => {
    setShowProgress(false);
    if (selectedScene) loadAnchors();
    toast.success("Storyboard generation complete!");
  };

  const selectedAnchor = anchors.find(a => a.id === selectedAnchorId);

  // =================================================================

  const handleDetectScenes = async () => {
    if (!script?.content) {
      toast.error("No script content to analyze");
      return;
    }

    setDetecting(true);
    try {
      const data = await redetectScenes({
        content: script.content,
        script_id: script.id
      });

      toast.success(`Detected ${data.scene_count} scenes`);
      addNotification({
        type: NOTIFICATION_TYPES.SUCCESS,
        category: NOTIFICATION_CATEGORIES.SCENES,
        title: 'Scene Detection Complete',
        message: `Found ${data.scene_count} scenes in your script`
      });
      onUpdate();
    } catch (error) {
      toast.error("Scene detection failed");
      addNotification({
        type: NOTIFICATION_TYPES.ERROR,
        category: NOTIFICATION_CATEGORIES.SCENES,
        title: 'Scene Detection Failed',
        message: 'Could not detect scenes in the script'
      });
    } finally {
      setDetecting(false);
    }
  };

  return (
    <>
    <div className="h-full flex">
      {/* Collapsible Sidebar */}
      <AnimatePresence initial={false}>
        {!sidebarCollapsed && (
          <motion.div
            initial={{ width: 0, opacity: 0 }}
            animate={{ width: 300, opacity: 1 }}
            exit={{ width: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="border-r border-[var(--border-subtle)] bg-[var(--bg-surface)] flex flex-col flex-shrink-0 overflow-hidden"
          >
            <div className="panel-header">
              <div className="flex items-center justify-between w-full">
                <div>
                  <h2 className="panel-title">Scenes</h2>
                  <p className="text-xs text-[var(--text-muted)] mt-0.5">{scenes.length} scenes</p>
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    onClick={handleDetectScenes}
                    disabled={detecting || !script?.content}
                    className="btn-ghost btn-icon-sm"
                    size="sm"
                    data-testid="detect-scenes-btn"
                    title="Re-detect Scenes"
                  >
                    {detecting ? <Loader2 size={14} className="animate-spin" /> : <Wand2 size={14} />}
                  </Button>
                  <Button
                    onClick={() => setSidebarCollapsed(true)}
                    className="btn-ghost btn-icon-sm"
                    size="sm"
                    title="Collapse sidebar"
                  >
                    <PanelLeftClose size={14} />
                  </Button>
                </div>
              </div>
            </div>

            {/* Auto Generate banner — below header, above search */}
            {scenes.length > 0 && (
              <div className="px-4 py-2.5 border-b border-[var(--border-subtle)] bg-gradient-to-r from-[rgba(212,168,83,0.05)] to-transparent">
                <Button
                  onClick={handleAutoGenerateFull}
                  className="btn-primary w-full text-xs h-8"
                  size="sm"
                  title="Auto Generate Storyboard for All Scenes"
                >
                  <Sparkles size={13} className="mr-1.5" />
                  Auto Generate All Scenes
                </Button>
              </div>
            )}

            {/* Search bar */}
            <div className="px-4 py-2 border-b border-[var(--border-subtle)]">
              <div className="relative">
                <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)] pointer-events-none z-10" />
                <Input
                  value={sceneSearch}
                  onChange={(e) => setSceneSearch(e.target.value)}
                  placeholder="Search scenes..."
                  className="input h-7 text-xs"
                  style={{ paddingLeft: '2.25rem' }}
                  data-testid="search-scenes"
                />
              </div>
            </div>

            <ScrollArea className="flex-1">
              <div className="p-5">
                {filteredScenes.length === 0 ? (
                  <div className="empty-state py-12">
                    <div className="empty-state-icon mx-auto">
                      <IconScenes size={28} />
                    </div>
                    <h3 className="text-sm font-semibold text-[var(--text-primary)] mb-1">
                      {sceneSearch ? 'No matches' : 'No Scenes'}
                    </h3>
                    <p className="text-xs text-[var(--text-muted)]">
                      {sceneSearch ? `No scenes match "${sceneSearch}"` : 'Add a script first'}
                    </p>
                  </div>
                ) : (
                  filteredScenes.map((scene, index) => (
                    <motion.div
                      key={scene.number}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.02 }}
                      className="mb-4"
                    >
                      <SceneCard
                        scene={scene}
                        characters={characters}
                        isSelected={selectedScene?.number === scene.number}
                        onClick={() => setSelectedScene(scene)}
                      />
                    </motion.div>
                  ))
                )}
              </div>
            </ScrollArea>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Scene Header */}
        <div className="h-14 px-4 border-b border-[var(--border-subtle)] bg-[var(--bg-surface)] flex items-center gap-3 flex-shrink-0">
          {sidebarCollapsed && (
            <Button
              onClick={() => setSidebarCollapsed(false)}
              className="btn-ghost btn-icon-sm"
              size="sm"
              title="Expand sidebar"
            >
              <PanelLeft size={14} />
            </Button>
          )}

          {selectedScene ? (
            <>
              <div className="flex items-center gap-2">
                <span className="chip chip-gold">Scene {selectedScene.number}</span>
                {selectedScene.type && (
                  <span className={`chip ${selectedScene.type.includes('EXT') ? 'chip-teal' : ''}`}>
                    {selectedScene.type.replace('.', '')}
                  </span>
                )}
                {selectedScene.time_of_day && (
                  <span className="chip">{selectedScene.time_of_day}</span>
                )}
              </div>
              <h2 className="text-sm font-semibold text-[var(--text-primary)] truncate flex-1">
                {selectedScene.heading || `Scene ${selectedScene.number}`}
              </h2>
            </>
          ) : (
            <h2 className="text-sm font-medium text-[var(--text-muted)]">Select a scene</h2>
          )}
        </div>

        {/* Content Area */}
        {selectedScene ? (
          <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
            <TabsList className="px-4 py-2 border-b border-[var(--border-subtle)] bg-[var(--bg-surface)] flex-shrink-0">
              <TabsTrigger value="design" className="data-[state=active]:bg-[var(--bg-elevated)]">
                <Settings2 size={14} className="mr-1.5" />
                Scene Design
              </TabsTrigger>
              <TabsTrigger value="shots" className="data-[state=active]:bg-[var(--bg-elevated)]">
                <Film size={14} className="mr-1.5" />
                Script & Shots
              </TabsTrigger>
              <TabsTrigger value="screenplay" className="data-[state=active]:bg-[var(--bg-elevated)]">
                <BookOpen size={14} className="mr-1.5" />
                Visual Screenplay
              </TabsTrigger>
              <TabsTrigger value="intelligence" className="data-[state=active]:bg-[var(--bg-elevated)]">
                <Brain size={14} className="mr-1.5" />
                Intelligence
              </TabsTrigger>
              <TabsTrigger value="continuity" className="data-[state=active]:bg-[var(--bg-elevated)]" data-testid="continuity-tab">
                <ShieldCheck size={14} className="mr-1.5" />
                Continuity
              </TabsTrigger>
            </TabsList>

            <TabsContent value="design" className="flex-1 overflow-y-auto m-0">
              <EnhancedSceneDesignTab
                scene={selectedScene}
                characters={characters}
                locations={locations}
                projectId={script?.project_id}
                onUpdate={onUpdate}
              />
            </TabsContent>

            <TabsContent value="shots" className="flex-1 overflow-hidden m-0">
              <div className="h-full flex">
                {/* Script Panel with Anchor-based Text Selection */}
                <AnimatePresence initial={false}>
                  {!scriptPanelCollapsed && (
                    <motion.div
                      initial={{ width: 0, opacity: 0 }}
                      animate={{ width: "55%", opacity: 1 }}
                      exit={{ width: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="border-r border-[var(--border-subtle)] flex flex-col overflow-hidden"
                    >
                      <div className="border-b border-[var(--border-subtle)] bg-[var(--bg-surface)]">
                        <div className="p-3 flex items-center justify-between">
                          <h3 className="font-medium text-sm text-[var(--text-primary)] flex items-center gap-2">
                            <Film size={14} />
                            Script
                            {loadingAnchors && (
                              <Loader2 size={12} className="animate-spin text-[var(--color-gold-400)]" />
                            )}
                            {anchors.length > 0 && !loadingAnchors && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[rgba(212,168,83,0.2)] text-[var(--color-gold-400)]">
                                {anchors.length} anchor{anchors.length !== 1 ? 's' : ''}
                              </span>
                            )}
                          </h3>
                          <Button
                            onClick={() => setScriptPanelCollapsed(true)}
                            className="btn-ghost btn-icon-sm"
                            size="sm"
                            title="Collapse script panel"
                          >
                            <PanelLeftClose size={14} />
                          </Button>
                        </div>
                        {/* Action buttons — full width row below title */}
                        <div className="px-3 pb-2.5 flex gap-2">
                          <Button
                            onClick={() => handleAutoGenerateScene(selectedScene.number)}
                            className="btn-primary flex-1 text-xs h-8"
                            size="sm"
                            title="Auto Generate Storyboard for this Scene"
                          >
                            <Sparkles size={13} className="mr-1.5" />
                            Auto Generate Scene
                          </Button>
                          <Button
                            onClick={handleAutoShotPlan}
                            disabled={autoPlanning || !selectedScene?.content}
                            className="btn-secondary flex-1 text-xs h-8"
                            size="sm"
                            title="AI plans shot anchors — you review & generate each"
                            data-testid="auto-shot-plan-btn"
                          >
                            {autoPlanning ? <Loader2 size={13} className="animate-spin mr-1.5" /> : <Wand2 size={13} className="mr-1.5" />}
                            Auto Plan Shots
                          </Button>
                        </div>
                      </div>

                      {/* Instructions hint when no anchors */}
                      {anchors.length === 0 && !loadingAnchors && (
                        <div className="px-4 py-2 bg-[rgba(212,168,83,0.05)] border-b border-[rgba(212,168,83,0.2)]">
                          <p className="text-[10px] text-[var(--color-gold-400)] flex items-center gap-1.5">
                            <Plus size={11} />
                            Select text in the script below, then click <strong>+ Add Shot</strong> to create shot anchors
                          </p>
                        </div>
                      )}

                      <AnchoredScriptView
                        content={selectedScene.content}
                        heading={selectedScene.heading}
                        sceneNumber={selectedScene.number}
                        projectId={script?.project_id}
                        anchors={anchors}
                        selectedAnchorId={selectedAnchorId}
                        onAnchorSelect={setSelectedAnchorId}
                        onAnchorCreate={handleAnchorCreate}
                        onAnchorDelete={handleAnchorDelete}
                      />
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Shot Designer Panel */}
                <div className={`${scriptPanelCollapsed ? 'w-full' : 'flex-1'} flex flex-col bg-[var(--bg-surface)]`}>
                  {scriptPanelCollapsed && (
                    <div className="p-2 border-b border-[var(--border-subtle)]">
                      <Button
                        onClick={() => setScriptPanelCollapsed(false)}
                        className="btn-ghost text-xs"
                        size="sm"
                      >
                        <PanelLeft size={14} />
                        <span className="ml-1">Show Script</span>
                      </Button>
                    </div>
                  )}

                  {showBlocking ? (
                    <ShotBlockingView
                      scene={selectedScene}
                      characters={characters}
                      projectId={script?.project_id}
                      anchor={selectedAnchor}
                      scriptContext={selectedAnchor?.selected_text || ""}
                      onClose={() => setShowBlocking(false)}
                      onShotGenerated={() => loadAnchors()}
                    />
                  ) : showQuickDraw ? (
                    <EmbeddedQuickDraw
                      onBack={() => setShowQuickDraw(false)}
                      characters={characters}
                      locations={locations}
                      onGenerate={() => {}}
                      segmentText={selectedAnchor?.selected_text || ""}
                      segmentNumber={selectedAnchor ? 1 : undefined}
                    />
                  ) : (
                    <AnchorShotPanel
                      anchor={selectedAnchor}
                      onClose={() => setSelectedAnchorId(null)}
                      onUpdate={loadAnchors}
                      onOpenQuickDraw={() => setShowQuickDraw(true)}
                      onOpenBlocking={() => setShowBlocking(true)}
                      SHOT_TYPES={SHOT_TYPES}
                      CAMERA_ANGLES={CAMERA_ANGLES}
                      CAMERA_MOVEMENTS={CAMERA_MOVEMENTS}
                      LIGHTING_STYLES={LIGHTING_STYLES}
                      LENS_OPTIONS={LENS_OPTIONS}
                    />
                  )}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="screenplay" className="flex-1 overflow-hidden m-0">
              <VisualScreenplayView
                content={selectedScene?.content}
                heading={selectedScene?.heading}
                sceneNumber={selectedScene?.number}
                projectId={script?.project_id}
                anchors={anchors}
                onAnchorSelect={(anchorId) => {
                  setSelectedAnchorId(anchorId);
                  setActiveTab("shots");
                }}
              />
            </TabsContent>

            <TabsContent value="intelligence" className="flex-1 overflow-hidden m-0">
              <ScriptIntelligence
                projectId={script?.project_id}
                scriptContent={selectedScene?.content}
              />
            </TabsContent>
            <TabsContent value="continuity" className="flex-1 overflow-hidden m-0">
              <ContinuityReport
                scene={selectedScene}
                projectId={script?.project_id}
              />
            </TabsContent>
          </Tabs>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center max-w-xs">
              <div className="empty-state-icon mx-auto mb-4">
                <IconScenes size={36} />
              </div>
              <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-2">Select a Scene</h3>
              <p className="text-sm text-[var(--text-muted)]">
                Choose a scene to design characters, set locations, and create shots.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>

    <AutoStoryboardPreflight
      open={showAutoGenerate}
      onOpenChange={setShowAutoGenerate}
      projectId={script?.project_id}
      sceneNumber={autoGenScene}
      onGenerationStarted={handleGenerationStarted}
    />

    {showProgress && script?.project_id && (
      <AutoStoryboardProgress
        projectId={script.project_id}
        onComplete={handleGenerationComplete}
        onDismiss={() => setShowProgress(false)}
      />
    )}
    </>
  );
}
